import { DropzoneOptions, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Button, cn } from '@sib/ui';
import { FileUploadStore } from '@spendbrain/stores/fileUploadStore';
import { DROPZONE_CONFIG, useOnDropHandler } from '@spendbrain/utils/files';

export interface UploadDocumentsDropzoneProps {
  allowedFormats: string;
  fileUploadStore: FileUploadStore;
  maxFilesSize: number;
  maxFilesNumber: number;
  disabled?: boolean;
  dropzoneConfig?: Partial<DropzoneOptions>;
}

export const UploadDocumentsDropzone = ({
  allowedFormats,
  fileUploadStore,
  disabled,
  maxFilesSize,
  maxFilesNumber,
  dropzoneConfig,
}: UploadDocumentsDropzoneProps) => {
  const { t } = useTranslation();
  const { addFiles } = fileUploadStore;
  const { onDrop } = useOnDropHandler({
    addFiles,
    maxSize: `${maxFilesSize} MB`,
  });
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    ...(dropzoneConfig || DROPZONE_CONFIG),
    onDrop,
    disabled,
  });

  return (
    <div className="flex items-center justify-between gap-1">
      <div className="flex flex-col gap-1 font-bold">
        <label className="text-primary text-sm">
          {t('uploadDocuments.title')}
        </label>
        <p className="text-muted-foreground text-xs leading-4 font-semibold">
          {t('uploadDocuments.description', {
            formats: allowedFormats,
          })}
        </p>
        <div className="flex gap-2">
          <p className="text-muted-foreground text-xs leading-4 font-semibold">
            {t('uploadDocuments.maxFilesNumber', {
              number: maxFilesNumber,
            })}
          </p>
          <p className="text-muted-foreground text-xs leading-4 font-semibold">
            {t('uploadDocuments.maxFilesSize', {
              size: maxFilesSize,
            })}
          </p>
        </div>
      </div>
      <div
        {...getRootProps()}
        className={cn(
          'relative flex w-3/4 cursor-pointer items-center justify-center rounded-lg border-dashed border-[#E2E8F0] p-2.5 text-center transition-colors sm:p-4 md:border-2',
          isDragActive ? 'border-primary' : 'border-[#64748b80]',
          disabled && 'cursor-not-allowed'
        )}
      >
        <input {...getInputProps()} />
        <p className="text-muted-foreground hidden text-xs leading-4 font-bold md:block md:max-w-5 lg:max-w-full">
          {t('uploadDocuments.dragAndDrop')}
        </p>

        <Button
          variant="outline"
          type="button"
          className="absolute right-2"
          disabled={disabled}
        >
          {t('uploadDocuments.browse')}
        </Button>
      </div>
    </div>
  );
};
